// ** MUI Imports
import { Skeleton } from '@mui/material'

// ** Type Imports
import {
  ActivePeriodLengthType,
  ApplicationStageType,
  DiscountFormStateType,
  JourneyLineStepConfigType,
  JourneyStepType,
  PaymentModeConcessionConfigType,
  ProgressiveSummaryDataType
} from 'src/lib/types/discounts'
import { useEffect, useState } from 'react'

import { getTimePeriodString, numberWithCommas } from 'src/lib/utils/helpers'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import { QuantityDiscountorBlockConfigType } from 'src/lib/types/discounts'
import CalloutCardPreview from '../discount-description-editor/CalloutCardPreview'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ProgressiveDiscountSummaryProps {
  formState: DiscountFormStateType | undefined
  currentStep: JourneyStepType
  journeyLineConfig: JourneyLineStepConfigType[] | undefined
  isEditMode: boolean
  currentStepIndex: number
  periodLength: ActivePeriodLengthType | undefined
  applicationStage: ApplicationStageType | undefined
}

const ProgressiveDiscountSummary: React.FC<ProgressiveDiscountSummaryProps> = ({
  formState,
  currentStep,
  journeyLineConfig,
  isEditMode,
  currentStepIndex,
  periodLength,
  applicationStage
}) => {
  const { setup, conditions, customerEligibility, usageLimits, preview } = formState || {}

  const isCalloutCard = setup?.applicationConfig?.valueType === 'CALLOUT_CARD'

  const discountIdentifier = isCalloutCard
    ? 'Callout card'
    : Boolean(setup?.applicationMethodConfig?.value?.length)
    ? setup?.applicationMethodConfig?.value
    : 'No discount code yet.'

  const [progessiveSummaryData, setProgressiveSummaryData] = useState<ProgressiveSummaryDataType[]>()

  const [maxStepReached, setMaxStepReached] = useState<number>(0)

  useEffect(() => {
    if (typeof formState === 'undefined') return
    setProgressiveSummaryData(createProgressiveSummaryData())

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, currentStep])

  useEffect(() => {
    if (typeof currentStepIndex === 'undefined') return
    setMaxStepReached(Math.max(currentStepIndex, maxStepReached))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex])

  useEffect(() => {
    setProgressiveSummaryData(createProgressiveSummaryData())

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxStepReached])

  const createProgressiveSummaryData = () => {
    const { applicationConfig, applicationMethodConfig } = setup || {}
    const { offerValueConfig, entitlementsConfig, discountGenerationConfig } = applicationConfig || {}
    const { thresholdConfig, prerequisitesConfig } = conditions || {}
    const { discountUsesConfig, stackabilityType, activePeriodConfig } = usageLimits || {}
    const { isDiscoverable, descriptionConfig } = preview || {}

    const getCoveredItemsCount = () => {
      const catalogueType = entitlementsConfig?.valueType
      let catalogueCount = 0
      let collectionCount = 0
      entitlementsConfig?.quantityDistributorConfig?.forEach(
        (item: QuantityDiscountorBlockConfigType, index: number) => {
          if (!item?.itemsConfig?.catalogueMetadata?.length) return
          catalogueCount += item?.itemsConfig?.catalogueMetadata?.length
        }
      )
      entitlementsConfig?.quantityDistributorConfig?.forEach(
        (item: QuantityDiscountorBlockConfigType, index: number) => {
          if (!item?.itemsConfig?.items?.length) return
          collectionCount += item?.itemsConfig?.items?.length
        }
      )
      return catalogueType === 'COLLECTION' ? collectionCount : catalogueCount
    }

    const getOfferDetailsForSpecificModes = () => {
      const paymentModeConcessions = applicationConfig?.offerValueConfig?.paymentModeConcessions
      const concessionsSummaryList = paymentModeConcessions?.map(
        (concession: PaymentModeConcessionConfigType, index: number) => {
          if (Boolean(concession?.paymentModeCheckboxConfig?.valueType)) {
            if (offerValueConfig?.valueType === 'FREEBIE' && offerValueConfig?.paymentModeCoverageType === 'SPECIFIC') {
              return `${concession?.valueType}: ${100}${offerValueConfig?.valueType === 'FREEBIE' ? '%' : ''} off on 
                    ${
                      entitlementsConfig?.valueType === 'COLLECTION'
                        ? `products from ${getCoveredItemsCount()} specific ${entitlementsConfig?.valueType?.toLocaleLowerCase()}(s)`
                        : `${getCoveredItemsCount()} specific ${entitlementsConfig?.valueType.toLocaleLowerCase()}(s)`
                    }`
            }
            return `${concession?.valueType}: ${offerValueConfig?.valueType === 'FLAT' ? '₹' : ''}${
              concession?.concessionAmount ?? 0
            }${offerValueConfig?.valueType === 'PERCENTAGE' ? '%' : ''} off
                    ${
                      offerValueConfig?.valueType === 'PERCENTAGE_CAPPED'
                        ? `upto ₹${concession?.maxConcessionAmount}`
                        : ''
                    }`
          }
          return undefined
        }
      )

      return concessionsSummaryList?.filter((element: any) => element !== undefined) ?? []
    }

    const createDescriptionBullets = () => {
      switch (applicationConfig?.valueType) {
        case 'PREPAID':
          return offerValueConfig?.paymentModeCoverageType === 'ALL'
            ? `${offerValueConfig?.valueType === 'FLAT' ? '₹' : ''}${
                Boolean(offerValueConfig?.value) ? offerValueConfig?.value : 0
              }${
                offerValueConfig?.valueType === 'PERCENTAGE' || offerValueConfig?.valueType === 'FREEBIE' ? '%' : ''
              } off 
                    ${
                      Boolean(offerValueConfig?.capCheckboxConfig?.valueType)
                        ? `upto ₹${offerValueConfig?.cappedValue}`
                        : ''
                    }
                    ${
                      Boolean(entitlementsConfig?.valueType === 'ALL')
                        ? ''
                        : `on 
                    ${
                      entitlementsConfig?.valueType === 'COLLECTION'
                        ? `products from ${getCoveredItemsCount()} specific ${entitlementsConfig?.valueType?.toLocaleLowerCase()}(s)`
                        : `${getCoveredItemsCount()} specific ${entitlementsConfig?.valueType.toLocaleLowerCase()}(s)`
                    }`
                    }`
            : getOfferDetailsForSpecificModes()
        case 'AMOUNT':
          return `${offerValueConfig?.valueType === 'FLAT' ? '₹' : ''}${
            Boolean(offerValueConfig?.value) ? offerValueConfig?.value : 0
          }${offerValueConfig?.valueType === 'PERCENTAGE' ? '%' : ''} off 
                    ${
                      Boolean(offerValueConfig?.capCheckboxConfig?.valueType)
                        ? `upto ₹${offerValueConfig?.cappedValue}`
                        : ''
                    }
                    ${
                      Boolean(entitlementsConfig?.valueType === 'ALL')
                        ? ''
                        : `on 
                    ${
                      entitlementsConfig?.valueType === 'COLLECTION'
                        ? `products from ${getCoveredItemsCount()} specific ${entitlementsConfig?.valueType?.toLocaleLowerCase()}(s)`
                        : `${getCoveredItemsCount()} specific ${entitlementsConfig?.valueType.toLocaleLowerCase()}(s)`
                    }`
                    }`

        case 'BXGY':
          return `Get ${offerValueConfig?.quantity} items 
                    ${
                      offerValueConfig?.value === 100
                        ? 'for free'
                        : `at ${Boolean(offerValueConfig?.value) ? offerValueConfig?.value : 0}% off`
                    } 
                    ${
                      Boolean(offerValueConfig?.capCheckboxConfig?.valueType)
                        ? `upto ₹${offerValueConfig?.cappedValue}`
                        : ''
                    }`

        case 'BUNDLE':
          return `Set the order value to ₹${Boolean(offerValueConfig?.value) ? offerValueConfig?.value : 0} for
                    ${entitlementsConfig?.valueType === 'ALL' ? 'any' : ''} ${offerValueConfig?.quantity} 
                    ${entitlementsConfig?.valueType === 'PRODUCT' ? 'specific' : ''} product(s) 
                    ${entitlementsConfig?.valueType === 'COLLECTION' ? 'in specific collection(s)' : ''}`
      }
    }

    const creatConditionBullets = () => {
      if (applicationConfig?.valueType === 'BUNDLE') {
        return 'Applicable on all carts'
      }

      switch (thresholdConfig?.valueType) {
        case 'QUANTITY':
          const minCount = Boolean(thresholdConfig?.minValue)
            ? thresholdConfig?.minValue
            : prerequisitesConfig?.quantityDistributorConfig?.reduce((accumulator, currentValue) => {
                return accumulator + (currentValue?.quantity ?? 0)
              }, 0)
          return `${
            Boolean(thresholdConfig?.maxCheckboxConfig?.valueType)
              ? `For purchases with ${prerequisitesConfig?.valueType === 'ALL' ? 'any' : ''} 
                        ${Boolean(thresholdConfig?.minValue) ? thresholdConfig?.minValue : 0} to ${
                  Boolean(thresholdConfig?.maxValue) ? thresholdConfig?.maxValue : 0
                } 
                        ${
                          prerequisitesConfig?.valueType === 'ALL'
                            ? 'item(s)'
                            : prerequisitesConfig?.valueType === 'PRODUCT'
                            ? 'specific product(s)'
                            : 'products in specific collection(s)'
                        }`
              : `Minimum purchase of ${prerequisitesConfig?.valueType === 'ALL' ? 'any' : ''} 
                        ${minCount} 
                        ${
                          prerequisitesConfig?.valueType === 'ALL'
                            ? 'item(s)'
                            : prerequisitesConfig?.valueType === 'PRODUCT'
                            ? 'specific product(s)'
                            : 'products in specific collection(s)'
                        }`
          }`

        case 'AMOUNT':
          const minAmount = Boolean(thresholdConfig?.minValue)
            ? thresholdConfig?.minValue
            : prerequisitesConfig?.quantityDistributorConfig?.reduce((accumulator, currentValue) => {
                return accumulator + (currentValue?.price ?? 0)
              }, 0)
          return `${
            Boolean(thresholdConfig?.maxCheckboxConfig?.valueType)
              ? `For purchases of ${
                  prerequisitesConfig?.valueType === 'ALL'
                    ? 'any item'
                    : prerequisitesConfig?.valueType === 'PRODUCT'
                    ? 'specific product(s)'
                    : 'products in specific collection(s)'
                } worth ₹${Boolean(thresholdConfig?.minValue) ? thresholdConfig?.minValue : 0} to ₹${
                  Boolean(thresholdConfig?.maxValue) ? thresholdConfig?.maxValue : 0
                }`
              : `Minimum purchase of ₹${minAmount} of 
                        ${
                          prerequisitesConfig?.valueType === 'ALL'
                            ? 'any item'
                            : prerequisitesConfig?.valueType === 'PRODUCT'
                            ? 'specific product(s)'
                            : 'products in specific collection(s)'
                        }`
          }`

        case 'NONE':
          return 'Applicable on all carts'
      }
    }

    const createEligibilityBullets = () => {
      switch (customerEligibility?.valueType) {
        case 'ALL':
          return 'For all customers'

        case 'FIRST':
          return `For first-time customers`

        case 'CONDITIONAL':
          return `For customers with 
                        ${
                          Boolean(customerEligibility?.maxOrderCountCheckboxConfig?.valueType)
                            ? `${customerEligibility?.minOrderCount} to ${customerEligibility?.maxOrderCount} orders`
                            : `a minimum of ${customerEligibility?.minOrderCount} order(s)`
                        }`

        case 'SPECIFIC':
          return 'For specific customers'
        case 'SEGMENTS':
          return 'For specific customer segments'
      }
    }

    const createUsageLimitBullets = () => {
      // return `${!Boolean(discountUsesConfig?.maxUsesCheckboxConfig?.valueType) && !Boolean(discountUsesConfig?.maxUsesPerCustomerCheckboxConfig?.valueType) ? 'No usage limit' : ''}
      // ${Boolean(discountUsesConfig?.maxUsesCheckboxConfig?.valueType) ? `Limit to ${discountUsesConfig?.maxUses} uses` : ''}
      // ${Boolean(discountUsesConfig?.maxUsesCheckboxConfig?.valueType) && Boolean(discountUsesConfig?.maxUsesPerCustomerCheckboxConfig?.valueType) ? ', ' : ''}
      // ${Boolean(discountUsesConfig?.maxUsesPerCustomer) ? `Limit to ${discountUsesConfig?.maxUsesPerCustomer}` : ''}`
      return `${
        !Boolean(discountUsesConfig?.maxUsesCheckboxConfig?.valueType) &&
        !Boolean(discountUsesConfig?.maxUsesPerCustomer)
          ? 'No usage limit'
          : ''
      }
            ${
              Boolean(discountUsesConfig?.maxUsesCheckboxConfig?.valueType)
                ? `Limit to ${discountUsesConfig?.maxUses} uses`
                : ''
            }
            ${
              Boolean(discountUsesConfig?.maxUsesCheckboxConfig?.valueType) &&
              Boolean(discountUsesConfig?.maxUsesPerCustomer)
                ? ', '
                : ''
            }
            ${
              Boolean(discountUsesConfig?.maxUsesPerCustomer)
                ? `Limit to ${discountUsesConfig?.maxUsesPerCustomer} uses per customer`
                : ''
            }`
    }

    const createActivePeriodBullets = () => {
      const hasExpiry = Boolean(activePeriodConfig?.endDataCheckboxConfig?.valueType)
      const activeDates = getTimePeriodString(activePeriodConfig?.startDate, activePeriodConfig?.endDate, hasExpiry)
      if (Boolean(periodLength === 'SHORT')) return `Active for ${activePeriodConfig?.activePeriod} minutes`
      return `${activeDates}`
    }

    const renderCalloutCardPreview = () => {
      const longDescriptionArray = setup?.applicationConfig?.discountDescription?.discountLongDescription
        ?.replaceAll('\n', '')
        ?.split('•  ')
      return (
        <div className='pt-2'>
          <CalloutCardPreview
            title={setup?.applicationConfig?.discountDescription?.discountTitle ?? ''}
            header={setup?.applicationConfig?.discountDescription?.discountHeader ?? ''}
            description={setup?.applicationConfig?.discountDescription?.discountDescription ?? ''}
            longDescription={longDescriptionArray}
          />
        </div>
      )
    }

    const summaryData: ProgressiveSummaryDataType[] | undefined = journeyLineConfig
      ?.filter((stepConfig: JourneyLineStepConfigType, index: number) =>
        Boolean(isEditMode) ? true : index <= maxStepReached
      )
      ?.map((filteredStepConfig: JourneyLineStepConfigType, index: number) => {
        switch (filteredStepConfig?.step) {
          case 'setup':
            if (isCalloutCard)
              return {
                step: 'preview',
                configInfoData: [renderCalloutCardPreview()]
              }
            return {
              step: filteredStepConfig?.step,
              configInfoData: [
                `${
                  applicationStage === 'POST_ORDER_PLACEMENT'
                    ? 'Customer will be presented this discount after placing a COD order'
                    : applicationMethodConfig?.valueType === 'MANUAL'
                    ? 'Customer will enter a discount code'
                    : 'Discount will be applied automatically'
                }`,
                createDescriptionBullets()
              ]
            }
          case 'condition':
            return {
              step: filteredStepConfig?.step,
              configInfoData: [creatConditionBullets()]
            }
          case 'eligibility':
            return {
              step: filteredStepConfig?.step,
              configInfoData: [createEligibilityBullets()]
            }
          case 'limits':
            return {
              step: filteredStepConfig?.step,
              configInfoData: [
                createUsageLimitBullets(),
                stackabilityType?.valueType === 'ALL'
                  ? 'Combines with other discounts that have been set to combine'
                  : stackabilityType?.valueType === 'CUSTOM'
                  ? 'Combine with selected discounts'
                  : 'Doesn’t combine with other discounts',
                createActivePeriodBullets()
              ]
            }
          case 'preview':
            return {
              step: filteredStepConfig?.step,
              configInfoData: [
                isDiscoverable
                  ? 'Coupon will be shown to eligible customers'
                  : 'Coupon will not be shown to any customer'
              ]
            }
          default:
            return {
              step: filteredStepConfig?.step,
              configInfoData: [
                `${
                  applicationMethodConfig?.valueType === 'MANUAL'
                    ? 'Customer will enter a discount code'
                    : 'Discount will be applied automatically'
                }`,
                createDescriptionBullets()
              ]
            }
        }
      })

    return summaryData
  }

  const renderStepInfo = (stepData: ProgressiveSummaryDataType, index: number) => {
    return (
      <li key={`${index}_${stepData?.step}`} className='flex flex-col'>
        <div className='w-full'>
          <span className='text-xs font-medium uppercase text-coal-light'>{stepData?.step}</span>
          <ul className='flex flex-col list-disc gap-1'>
            {stepData?.configInfoData?.map(
              (stepInfo: string | React.ReactNode | (string | undefined)[] | undefined, index: number) => {
                return Boolean(typeof stepInfo === 'string') ? (
                  <li key={`${index}_${stepInfo}`} className='flex items-start gap-2'>
                    • <span className='text-sm mt-0.5 font-medium text-coal-dark'>{stepInfo}</span>
                  </li>
                ) : Array.isArray(stepInfo) ? (
                  stepInfo?.map((step: string | undefined, index: number) => {
                    return (
                      <li key={`${index}_${stepInfo}`} className='flex items-start gap-2'>
                        • <span className='text-sm mt-0.5 font-medium text-coal-dark'>{step}</span>
                      </li>
                    )
                  })
                ) : (
                  stepInfo
                )
              }
            )}
          </ul>
        </div>
      </li>
    )
  }

  return (
    <div className='w-full max-h-min'>
      {Boolean(typeof progessiveSummaryData !== 'undefined' && progessiveSummaryData?.length) ? (
        <LayoutContainer>
          <div className='flex flex-col w-full gap-2 divide-y-[0.5px]'>
            <span
              className={`text-ellipsis w-full overflow-hidden ${
                Boolean(setup?.applicationMethodConfig?.value?.length)
                  ? 'text-coal-dark text-sm font-semibold'
                  : 'text-coal-light text-sm font-medium'
              }`}
            >
              {discountIdentifier}
            </span>
            <div className='flex flex-col w-full gap-4 py-2'>
              {!isCalloutCard && <span className={`text-carbon text-base font-medium`}>Discount properties</span>}
              <ul className='flex flex-col w-full gap-4'>
                {progessiveSummaryData?.map((stepData: ProgressiveSummaryDataType, index: number) =>
                  renderStepInfo(stepData, index)
                )}
              </ul>
            </div>
          </div>
        </LayoutContainer>
      ) : (
        <></>
      )}
      {isEditMode && typeof progessiveSummaryData === 'undefined' ? (
        <>
          <Skeleton variant='rectangular' sx={{ borderRadius: '16px' }} width={'100%'} height={200} />
        </>
      ) : (
        <></>
      )}
    </div>
  )
}

export default ProgressiveDiscountSummary
